// /demo - start a synthetic sandbox, join one from another device, see and switch
// personas, leave. No data page works without a persona session (App.tsx guard).
import { type FormEvent, useId, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { RuleRefs } from '@/components/evidence'
import { Badge, Banner, Button, Card, Field, PageHeader, SectionTitle, Select, Spinner, TextInput } from '@/components/ui'
import type { DemoPersona } from '@/domain/types'
import { useSession } from '@/lib/queries'
import { LinkButton } from '@/features/home/LinkButton'
import { CopyButton, CredentialList, DemoError } from './parts'
import {
  DEMO_PERSONA_CATALOGUE,
  formatSandboxCode,
  LEGACY_PERSONA_CATALOGUE,
  muscatToday,
  normaliseSandboxCode,
  sandboxCodeProblem,
  PERSONA_USE_TEXT,
  personaUse,
  type PersonaUse,
  shortName,
  splitPersonas,
} from './personas'
import { useAsyncAction, useDemoActions, useDemoStatus, useQrDataUrl } from './useDemo'

/**
 * A join failure is kept here, not in the join form: joining a different sandbox first
 * leaves the current one, which swaps the active view for the start/join view while the
 * request is still running, so the form that sent it may no longer exist.
 */
type JoinErrorState = { error: unknown; setError: (e: unknown) => void }

export function DemoPage() {
  const session = useSession()
  const [params] = useSearchParams()
  const joinParam = normaliseSandboxCode(params.get('join') ?? '')
  const [started, setStarted] = useState(false)
  const [joinError, setJoinError] = useState<unknown>(null)
  const join: JoinErrorState = { error: joinError, setError: setJoinError }
  const active = Boolean(session.activePersona && session.sandboxCode)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Synthetic demonstration"
        subtitle="Run the whole Class B permit lifecycle with fictitious people, sites and licences."
      />
      {active && session.sandboxCode ? (
        <ActiveDemo sandboxCode={session.sandboxCode} started={started && session.activePersona === 'PA'} join={join} />
      ) : (
        <NoDemo
          // The order depends on the link only, so it never flips (and remounts a form) mid-request.
          joinFirst={Boolean(joinParam)}
          joinCode={joinParam || session.sandboxCode || ''}
          join={join}
          onStarted={() => setStarted(true)}
        />
      )}
      <HowItWorks />
    </div>
  )
}

// -----------------------------------------------------------------------------
// No session: start or join
// -----------------------------------------------------------------------------
function NoDemo({ joinFirst, joinCode, join, onStarted }: { joinFirst: boolean; joinCode: string; join: JoinErrorState; onStarted: () => void }) {
  const actions = useDemoActions()
  const { busy, error, run } = useAsyncAction()
  const start = async () => {
    join.setError(null)
    const ok = await run('start', actions.start)
    if (ok) onStarted()
  }
  const startCard = (
    <Card accent="synthetic">
      <SectionTitle>Start a new demonstration</SectionTitle>
      <p className="mb-4 text-sm text-ink">
        Creates a private sandbox: a fictional site with its areas and equipment, and 14 fictional personas. You start as
        Synthetic Ahmed Al-Harthy, the Permit Applicant.
      </p>
      <Button block onClick={() => void start()} loading={busy === 'start'} disabled={busy !== null}>
        Start a synthetic demonstration
      </Button>
      <DemoError error={error} title="Could not start the demonstration" />
    </Card>
  )
  const joinCard = <JoinCard initialCode={joinCode} join={join} />
  // Arriving from a join link or QR code: the join form comes first.
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {joinFirst ? joinCard : startCard}
      {joinFirst ? startCard : joinCard}
    </div>
  )
}

function JoinCard({ initialCode, join, leavesCurrent }: { initialCode: string; join: JoinErrorState; leavesCurrent?: boolean }) {
  const navigate = useNavigate()
  const actions = useDemoActions()
  const { busy, run } = useAsyncAction()
  const [code, setCode] = useState(initialCode)
  const [persona, setPersona] = useState('')
  const codeId = useId()
  const personaId = useId()
  const normalised = normaliseSandboxCode(code)
  const codeProblem = sandboxCodeProblem(normalised)
  const canJoin = normalised.length > 0 && codeProblem === null && persona !== '' && busy === null
  // Older sandboxes (before 0016) still have an Area Authority persona: listed last, and its
  // role says it is for older sandboxes only. The server refuses a code a sandbox lacks.
  const options = [...DEMO_PERSONA_CATALOGUE, ...LEGACY_PERSONA_CATALOGUE].map((p) => ({ value: p.code, label: `${shortName(p.name)} - ${p.role}` }))
  const { setError } = join

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canJoin) return
    setError(null)
    const ok = await run('join', async () => {
      try {
        await actions.join(normalised, persona)
      } catch (err) {
        setError(err)
        throw err
      }
    })
    if (ok) navigate('/')
  }

  return (
    <Card>
      <SectionTitle>Join on another device</SectionTitle>
      <p className="mb-4 text-sm text-ink">
        Someone has already started a demo? Enter their sandbox code and choose who you will act as on this device.
      </p>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field
          label="Sandbox code"
          htmlFor={codeId}
          hint={
            normalised && !codeProblem
              ? `Reads as ${formatSandboxCode(normalised)}.`
              : 'The 8 or 13 letters and numbers shown on the Demo page of the first device. Spaces and dashes are ignored.'
          }
          error={codeProblem}
          required
        >
          <TextInput
            id={codeId}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="ABCD EFGH JKMNP"
            aria-invalid={codeProblem ? true : undefined}
            className="code uppercase tracking-widest"
          />
        </Field>
        <Field label="Act as" htmlFor={personaId} required>
          <Select id={personaId} value={persona} onChange={(e) => setPersona(e.target.value)} options={options} placeholder="Choose a persona" />
        </Field>
        <p className="mb-3 text-xs text-ink-soft">
          If that persona is already in use on another device, it moves to this one and the other device loses it.
        </p>
        {leavesCurrent ? (
          <Banner tone="warn" className="mb-3">
            Joining another demo signs every persona on this device out of the current one.
          </Banner>
        ) : null}
        <Button type="submit" block disabled={!canJoin} loading={busy === 'join'}>
          Join this demo
        </Button>
      </form>
      <DemoError error={join.error} title="Could not join" />
    </Card>
  )
}

// -----------------------------------------------------------------------------
// Active session: code, personas, leave
// -----------------------------------------------------------------------------
function ActiveDemo({ sandboxCode, started, join }: { sandboxCode: string; started: boolean; join: JoinErrorState }) {
  const status = useDemoStatus()
  return (
    <>
      {started ? (
        <Banner tone="ok" title="Demonstration started">
          <p>You are acting as Synthetic Ahmed Al-Harthy, the Permit Applicant. Home shows the suggested walkthrough.</p>
          <div className="mt-2">
            <LinkButton to="/">Go to Home</LinkButton>
          </div>
        </Banner>
      ) : null}
      <SandboxCodeCard code={sandboxCode} />
      <Card>
        <SectionTitle>Personas</SectionTitle>
        <p className="mb-3 text-sm text-ink">
          Each persona is a separate sign-in bound to one fictional person. Acting as a persona signs this device in as them;
          what you can see and sign then depends on their synthetic licences.
        </p>
        {status.isLoading ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : null}
        <DemoError error={status.error} title="Could not load the personas" />
        {status.error ? (
          <Button variant="secondary" className="mt-2" onClick={() => void status.refetch()}>
            Try again
          </Button>
        ) : null}
        {status.data ? <PersonaDirectory personas={status.data.personas} /> : null}
      </Card>
      <details className="rounded-xl border border-line bg-card p-4 shadow-sm">
        <summary className="tap flex cursor-pointer items-center font-semibold text-ink">Join a different demo on this device</summary>
        <div className="mt-3">
          <JoinCard initialCode="" join={join} leavesCurrent />
        </div>
      </details>
      <LeaveCard code={sandboxCode} />
    </>
  )
}

function SandboxCodeCard({ code }: { code: string }) {
  const link = `${window.location.origin}/demo?join=${encodeURIComponent(code)}`
  const qr = useQrDataUrl(link)
  return (
    <Card accent="synthetic">
      <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Sandbox code</p>
          {/* Grouped for reading aloud; a long code wraps between groups, never inside one. */}
          <p className="code mt-1 select-all text-2xl font-bold tracking-[0.12em] text-ink sm:text-4xl">{formatSandboxCode(code)}</p>
          <p className="mt-3 text-sm text-ink">
            Use this code to add a second phone or laptop as another persona: open this app there, go to Demo, choose{' '}
            <span className="font-semibold">Join on another device</span>, enter the code and pick a persona. Or scan the QR
            code.
          </p>
          <p className="mt-1 text-xs text-ink-soft">Anyone with the code can join this sandbox. Share it only with the people taking part.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton text={code} label="Copy code" />
            <CopyButton text={link} label="Copy join link" />
          </div>
        </div>
        {qr ? (
          <img src={qr} alt="QR code that opens the join form with this sandbox code" className="h-40 w-40 shrink-0 rounded-lg border border-line bg-white" />
        ) : null}
      </div>
    </Card>
  )
}

const USE_TONE: Record<PersonaUse, 'ok' | 'warn' | 'neutral'> = { this_device: 'ok', elsewhere: 'warn', unused: 'neutral' }

function PersonaDirectory({ personas }: { personas: DemoPersona[] }) {
  const session = useSession()
  const navigate = useNavigate()
  const actions = useDemoActions()
  const { busy, error, run } = useAsyncAction()
  const [confirm, setConfirm] = useState<string | null>(null)
  const [target, setTarget] = useState<string | null>(null)
  const today = muscatToday()
  const { lifecycle, negative } = splitPersonas(personas)

  const act = async (p: DemoPersona) => {
    if (personaUse(p, session.boundPersonas) === 'elsewhere' && confirm !== p.persona_code) {
      setConfirm(p.persona_code)
      return
    }
    setConfirm(null)
    setTarget(p.display_name)
    const ok = await run(p.persona_code, () => actions.switchTo(p.persona_code))
    if (ok) navigate('/')
  }

  const card = (p: DemoPersona) => (
    <PersonaCard
      key={p.persona_code}
      p={p}
      today={today}
      use={personaUse(p, session.boundPersonas)}
      isActive={session.activePersona === p.persona_code}
      confirming={confirm === p.persona_code}
      busy={busy}
      onAct={() => void act(p)}
      onCancelConfirm={() => setConfirm(null)}
    />
  )

  return (
    <div className="space-y-4">
      <DemoError error={error} title={target ? `Could not switch to ${target}` : 'Could not switch persona'} />
      <div className="grid gap-3 sm:grid-cols-2">{lifecycle.map(card)}</div>
      {negative.length > 0 ? (
        <section aria-labelledby="negative-cases" className="rounded-xl border border-dashed border-line-strong p-3">
          <h3 id="negative-cases" className="display text-base font-semibold text-ink">
            Negative test cases
          </h3>
          <p className="mb-3 text-sm text-ink-soft">
            These personas exist to show the server refusing: an expired, revoked or out-of-scope licence, and someone from an
            unrelated site who must not see these permits.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">{negative.map(card)}</div>
        </section>
      ) : null}
      <p className="text-xs text-ink-soft">
        All credentials shown are synthetic fixtures, not real licences or certificates. Whether a credential counts is decided
        by the server when someone signs.
      </p>
    </div>
  )
}

function PersonaCard({
  p,
  today,
  use,
  isActive,
  confirming,
  busy,
  onAct,
  onCancelConfirm,
}: {
  p: DemoPersona
  today: string
  use: PersonaUse
  isActive: boolean
  confirming: boolean
  busy: string | null
  onAct: () => void
  onCancelConfirm: () => void
}) {
  return (
    <div className={`flex flex-col rounded-xl border bg-white p-3 ${isActive ? 'border-brand ring-2 ring-brand/30' : 'border-line'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-ink">{p.display_name}</p>
        <Badge tone="brand" className="code">
          {p.persona_code}
        </Badge>
        {isActive ? <Badge tone="ok">You are acting as this persona</Badge> : null}
      </div>
      <p className="text-sm text-ink-soft">{p.organisation ?? 'No organisation'}</p>
      <p className="mt-1 text-sm text-ink">{p.description}</p>
      <div className="mt-2">
        <CredentialList persona={p} today={today} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
        <span>
          Fixture: <span className="code">{p.fixture_id ?? 'none'}</span>
        </span>
        <Badge tone={USE_TONE[use]}>
          {use === 'unused' ? 'Bound: no' : 'Bound: yes'} - {PERSONA_USE_TEXT[use]}
        </Badge>
      </div>
      <div className="mt-auto pt-3">
        {isActive ? null : confirming ? (
          <div className="space-y-2 rounded-lg border border-warn/30 bg-warn-light p-2">
            <p className="text-sm text-ink">
              {shortName(p.display_name)} is in use in another browser or device. Acting as them here moves the persona to this
              device, and the other device loses it.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={onAct} loading={busy === p.persona_code} disabled={busy !== null}>
                Move here and act
              </Button>
              <Button variant="secondary" onClick={onCancelConfirm}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button block variant="secondary" onClick={onAct} loading={busy === p.persona_code} disabled={busy !== null}>
            Act as this persona
          </Button>
        )}
      </div>
    </div>
  )
}

function LeaveCard({ code }: { code: string }) {
  const actions = useDemoActions()
  const { busy, error, run } = useAsyncAction()
  const [confirming, setConfirming] = useState(false)
  return (
    <Card>
      <SectionTitle>Leave the demo on this device</SectionTitle>
      <p className="mb-3 text-sm text-ink">
        Signs every persona on this device out. The sandbox stays on the server: you can join again with code{' '}
        <span className="code font-semibold">{formatSandboxCode(code)}</span>.
      </p>
      {confirming ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" onClick={() => void run('leave', actions.leave)} loading={busy === 'leave'}>
            Yes, leave the demo
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy !== null}>
            Stay
          </Button>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Leave demo
        </Button>
      )}
      <DemoError error={error} title="Could not leave cleanly" />
    </Card>
  )
}

function HowItWorks() {
  return (
    <Card>
      <SectionTitle>How the demonstration works</SectionTitle>
      <ul className="list-disc space-y-2 pl-5 text-sm text-ink">
        <li>
          <span className="font-semibold">Every persona is its own sign-in.</span> Switching persona changes which sign-in sends
          the next step. Use two devices to see two people work on one permit.
        </li>
        <li>
          <span className="font-semibold">The server decides who signed and when.</span> It takes the signer from the sign-in and
          the time from its own clock. The app never sends a name or a time as proof.
          <RuleRefs ruleIds={['SYS-005']} />
        </li>
        <li>
          <span className="font-semibold">Every check still runs:</span> licences and their scope, role separation and the permit
          rules. The negative-case personas show the server refusing.
        </li>
        <li>
          <span className="font-semibold">Nothing here is real.</span> The people, companies, sites and licences are fictitious.
          This demonstration cannot issue a real permit to work.
        </li>
      </ul>
      <p className="mt-3 text-sm">
        <Link to="/about" className="tap inline-flex items-center font-semibold text-brand-dark underline">
          About this system
        </Link>
      </p>
    </Card>
  )
}
